import React from 'react';
import { Link } from 'react-router-dom';
import RouteLine from '../components/RouteLine';

const services = [
  { icon: 'fas fa-ship', title: 'Shipbuilding & Construction', desc: 'Custom engineering and robust vessel fabrication tailored to demanding global shipping demands.' },
  { icon: 'fas fa-gas-pump', title: 'Bunkering Operations', desc: 'Top-tier bunkering ships for rent or sale, providing redefined and high-efficiency refueling solutions.' },
  { icon: 'fas fa-anchor', title: 'Fleet Expansion', desc: 'Strategic sourcing, custom enhancements, and asset scaling to elevate your operational footprint.' },
  { icon: 'fas fa-tools', title: 'Maintenance & Refurbishment', desc: 'Comprehensive dry dock support, structural overhauls, and expert ship repairs to maximize vessel lifespans.' },
  { icon: 'fas fa-users', title: 'Crew Arrangement & Management', desc: 'Deploying highly trained mariners and handling technical crew logistics under strict maritime standards.' },
  { icon: 'fas fa-clipboard-check', title: 'ISM & Quality Compliance', desc: 'Normative maritime management adhering to rigorous international safety and quality control guidelines.' },
];

const advantages = [
  { icon: 'fas fa-dollar-sign', title: 'Competitive Pricing', desc: 'Unparalleled, market-oriented rates tailored to maximize your fleet operational efficiency.' },
  { icon: 'fas fa-headset', title: 'Year-Round Support', desc: 'Dedicated 24/7 technical maritime specialists available to keep your fleet operational day or night.' },
  { icon: 'fas fa-shield-alt', title: 'Precision & Quality', desc: 'Rigorous engineering oversight and adherence to strict global safety management protocols.' },
  { icon: 'fas fa-globe', title: 'Global Access, Local Solutions', desc: 'Providing expansive maritime infrastructure solutions with reliable local execution.' },
];

const industries = [
  { icon: 'fas fa-oil-can', name: 'Oil & Gas Tankers' },
  { icon: 'fas fa-boxes', name: 'Bulk Carriers' },
  { icon: 'fas fa-ship', name: 'Multi-Purpose Vessels (MPP)' },
  { icon: 'fas fa-dharmachakra', name: 'Commercial Ferries' },
  { icon: 'fas fa-anchor', name: 'Bunkering Services' },
  { icon: 'fas fa-building', name: 'Maritime Logistics' },
];

const testimonials = [
  { initials: 'AM', name: 'Aiden Marsh', role: 'Ops Director, Northfield Marine', quote: 'East Star Marine delivered our custom bunkering vessel ahead of schedule. Their technical precision and quality control standards are exceptional.' },
  { initials: 'RT', name: 'Rina Tanaka', role: 'Fleet Procurement Lead, Hatori Shipping', quote: 'Their maintenance and refurbishment team brought our bulk carrier up to high-tier standard with unmatched turnaround times.' },
  { initials: 'CO', name: 'Carlos Ortega', role: 'Founder, Ortega Maritime Co.', quote: 'Managing crew logistics and compliance used to be our biggest bottleneck. East Star completely streamlined the process.' },
];

export default function Home() {
  return (
    <>
    {/* Hero */}
      <section 
        className="hero" 
        style={{
          position: 'relative',
          backgroundImage: `linear-gradient(rgba(10, 17, 34, 0.85), rgba(10, 17, 34, 0.75)), url('/image_d588e4.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          padding: '80px 0',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <div className="hero-bg-grid"></div>
        <div className="hero-glow"></div>
        
        {/* Responsive Container Wrapper */}
        <div 
          className="container hero-inner"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '40px',
            alignItems: 'center',
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}
        >
          {/* Left / Top Copy Column */}
          <div className="hero-copy" style={{ width: '100%' }}>
            <div className="eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-anchor"></i> Fueling the Future, Navigating with Precision
            </div>
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.2', margin: '20px 0' }}>
              Empowering Your Fleet, <em>Redefining Possibilities</em>.
            </h1>
            <p style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)', opacity: 0.9, lineHeight: '1.6', marginBottom: '30px' }}>
              East Star Marine specializes in providing top-tier bunkering ships for rent or sale, delivering unparalleled solutions to elevate your maritime operations, shipbuilding, and fleet management.
            </p>
            
            {/* Flexbox Actions for Wrapping on Mobile */}
            <div className="hero-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '40px' }}>
              <Link to="/contact" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                Contact Us Today <i className="fas fa-arrow-right" style={{ marginLeft: '8px' }}></i>
              </Link>
              <Link to="/services" className="btn btn-outline" style={{ whiteSpace: 'nowrap' }}>
                Explore Services
              </Link>
            </div>
            
            {/* Responsive Stats Grid */}
            <div 
              className="hero-stats" 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', 
                gap: '20px' 
              }}
            >
              <div className="hero-stat" style={{ display: 'flex', flexDirection: 'column' }}>
                <strong style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>2,000+</strong>
                <span style={{ fontSize: '13px', opacity: 0.8 }}>Skilled Specialists</span>
              </div>
              <div className="hero-stat" style={{ display: 'flex', flexDirection: 'column' }}>
                <strong style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>75K</strong>
                <span style={{ fontSize: '13px', opacity: 0.8 }}>Max Tonnage (T) Capacity</span>
              </div>
              <div className="hero-stat" style={{ display: 'flex', flexDirection: 'column' }}>
                <strong style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>400K+</strong>
                <span style={{ fontSize: '13px', opacity: 0.8 }}>Annual Capacity (T)</span>
              </div>
            </div>
          </div>

          {/* Right / Bottom UI Panel Column */}
          <div className="hero-panel" style={{ width: '100%', maxWidth: '500px', justifySelf: 'center' }}>
            <div className="route-card" style={{ width: '100%', boxSizing: 'border-box' }}>
              <div className="route-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <span>VESSEL PORTFOLIO <span className="tag">#ES-MARINE</span></span>
                <span>OPERATIONAL</span>
              </div>
              <RouteLine from="SHIPBUILDING" to="DEPLOYMENT" />
              <div className="route-card-stops" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="route-stop" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span className="dot" style={{ marginTop: '6px' }}></span>
                  <div>Custom Engineering & Design <b style={{ display: 'block', fontSize: '11px', opacity: 0.7 }}>PHASE 1</b></div>
                </div>
                <div className="route-stop" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span className="dot" style={{ marginTop: '6px' }}></span>
                  <div>Rigorous Quality Control & ISM Clearances <b style={{ display: 'block', fontSize: '11px', opacity: 0.7 }}>PHASE 2</b></div>
                </div>
                <div className="route-stop" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span className="dot muted" style={{ marginTop: '6px' }}></span>
                  <div style={{ opacity: 0.6 }}>High-Performance Sea Trials & Handover <b style={{ display: 'block', fontSize: '11px' }}>PHASE 3</b></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Trust strip */}
      <div className="trust-strip">
        <div className="container">
          <div className="trust-item"><i className="fas fa-anchor"></i> Deep-Water Coastline Infrastructure</div>
          <div className="trust-item"><i className="fas fa-certificate"></i> ISM & ISO-Aligned Operations</div>
          <div className="trust-item"><i className="fas fa-globe"></i> 24/7 Fleet Support</div>
          <div className="trust-item"><i className="fas fa-drafting-compass"></i> Elite Engineering Teams</div>
        </div>
      </div>

      {/* About snapshot */}
      <section className="section">
        <div className="container">
          <div className="split-grid reveal">
            <div>
              <div className="eyebrow"><i className="fas fa-compass"></i> Setting Sail with Quality</div>
              <h2 className="section-title">A Maritime Partner Built Around Excellence</h2>
              <p>East Star Marine brings together decades of collective team expertise to provide elite shipbuilding, ship repair, and comprehensive fleet management solutions for clients requiring top-tier reliability.</p>
              <ul className="checklist">
                <li><i className="fas fa-check-circle"></i> Market-oriented and customer-centric management philosophy</li>
                <li><i className="fas fa-check-circle"></i> Expansive maritime facility assets with deep-water frontier access</li>
                <li><i className="fas fa-check-circle"></i> Rigid compliance with international safety protocols and standards</li>
              </ul>
              <Link to="/about" className="btn btn-ghost">Learn Our Story <i className="fas fa-arrow-right"></i></Link>
            </div>
            <div className="about-visual">
              <div className="about-visual-badge"><strong>5/5</strong><span>RATED MARITIME SOLUTIONS</span></div>
              <div className="icon-tile-grid">
                <div className="icon-tile"><i className="fas fa-gas-pump"></i> Bunkering</div>
                <div className="icon-tile"><i className="fas fa-hammer"></i> Construction</div>
                <div className="icon-tile"><i className="fas fa-tools"></i> Repairs</div>
                <div className="icon-tile"><i className="fas fa-users"></i> Management</div>
              </div>
              <RouteLine from="FACILITY" to="OCEAN FRONT" light />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section section-alt">
        <div className="container">
          <div className="head-row">
            <div>
              <div className="eyebrow"><i className="fas fa-layer-group"></i> Comprehensive Core Solutions</div>
              <h2 className="section-title">Maximize Efficiency and Lifespan</h2>
            </div>
            <p className="section-subtitle" style={{ margin: 0 }}>From customized engineering to turnkey delivery and year-round operational support, your fleet is powered for the future.</p>
          </div>
          <div className="cards-grid">
            {services.map((s, i) => (
              <div key={i} className="service-card reveal">
                <span className="s-num">{String(i + 1).padStart(2, '0')}</span>
                <div className="s-icon"><i className={s.icon}></i></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link to="/services" className="service-link">Read More <i className="fas fa-arrow-right"></i></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="section section-dark">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto 16px' }}>
            <div className="eyebrow centered"><i className="fas fa-star"></i> Why East Star Marine</div>
            <h2 className="section-title centered">Pioneering Maritime Standards</h2>
          </div>
          <div className="advantage-grid">
            {advantages.map((a, i) => (
              <div key={i} className="advantage-card reveal">
                <i className={a.icon}></i>
                <h4>{a.title}</h4>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto' }}>
            <div className="eyebrow centered"><i className="fas fa-tasks"></i> Operational Workflow</div>
            <h2 className="section-title centered">Four Steps to Customized Solutions</h2>
          </div>
          <div className="process-rail">
            <div className="process-connector"></div>
            <div className="process-grid">
              {[
                ['01', 'Initial Consultation', 'Define your operational criteria, sizing specs, and fleet performance goals.'],
                ['02', 'Strategic Planning', 'We establish engineering frameworks, costing metrics, and regulatory baselines.'],
                ['03', 'Precision Fabrication', 'Vessels take shape using high-capacity workshops and advanced manufacturing machinery.'],
                ['04', 'Delivery & Support', 'Final inspections, delivery confirmation, and seamless handoff with continuous support.'],
              ].map(([num, title, desc], i) => (
                <div key={i} className="process-step reveal">
                  <span className="p-num">{num}</span>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-content">
            <div>
              <h3>Transform Your Fleet with Custom Solutions</h3>
              <p>Speak directly with a maritime infrastructure specialist at +86 152 5809 2554</p>
            </div>
            <Link to="/contact" className="btn btn-dark">Request a Consultation <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section section-alt">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto' }}>
            <div className="eyebrow centered"><i className="fas fa-anchor"></i> Portfolios We Engineer</div>
            <h2 className="section-title centered">Built for High-Tonnage Maritime Markets</h2>
          </div>
          <div className="industries-grid">
            {industries.map((ind, i) => (
              <div key={i} className="industry-chip reveal">
                <i className={ind.icon}></i>
                <span>{ind.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="stats-band">
        <div className="container">
          {[
            ['20', '+', 'Senior Engineers'],
            ['400K', 'T+', 'Annual Shipbuilding Capacity'],
            ['75K', 'T', 'Vessel Capacity Baseline'],
            ['100', '%', 'ISM Mode Adherence'],
          ].map(([num, suffix, label], i) => (
            <div key={i} className="stat-block">
              <strong>{num}<span>{suffix}</span></strong>
              <p>{label}</p>
            </div>
          ))}
        </div>
      </div>

      
    </>
  );
}